import { useEffect, useRef, useState } from 'react';
import type { SettingsControls } from '../hooks/useSettings';
import {
  KEY_BINDING_DEFINITIONS,
  bindingFromKeyboardEvent,
  formatBinding,
  type AppSettings,
  type GameAction,
} from '../settings/settings';

interface SettingsDialogProps {
  settings: AppSettings;
  controls: SettingsControls;
  onClose: () => void;
}

interface ToggleRowProps {
  checked: boolean;
  description: string;
  label: string;
  onChange: (checked: boolean) => void;
}

function ToggleRow({ checked, description, label, onChange }: ToggleRowProps) {
  return (
    <label className="toggle-row">
      <span><strong>{label}</strong><small>{description}</small></span>
      <input type="checkbox" role="switch" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="toggle-track" aria-hidden="true"><i /></span>
    </label>
  );
}

export function SettingsDialog({ settings, controls, onClose }: SettingsDialogProps) {
  const [listening, setListening] = useState<GameAction | null>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButton.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!listening) {
        if (event.code === 'Escape') onClose();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (event.code === 'Backspace' || event.code === 'Delete') {
        controls.resetKeyBinding(listening);
        setListening(null);
        return;
      }

      const binding = bindingFromKeyboardEvent(event);
      if (!binding) return;
      controls.setKeyBinding(listening, binding);
      setListening(null);
    };

    window.addEventListener('keydown', onKeyDown, listening ? { capture: true } : undefined);
    return () => window.removeEventListener('keydown', onKeyDown, listening ? { capture: true } : undefined);
  }, [controls, listening, onClose]);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="settings-dialog" role="dialog" aria-modal="true" aria-labelledby="settings-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="settings-header">
          <div>
            <span className="overlay-kicker">Game settings</span>
            <h2 id="settings-title">Settings</h2>
          </div>
          <button ref={closeButton} type="button" className="dialog-close" onClick={onClose} aria-label="Close settings">×</button>
        </header>

        <div className="settings-section">
          <div className="settings-section__heading">
            <div><h3>Keybinds</h3><p>Pick a control, then press any key or key combination.</p></div>
            <button type="button" className="text-button" onClick={controls.restoreDefaults}>Restore all defaults</button>
          </div>
          <div className="binding-list">
            {KEY_BINDING_DEFINITIONS.map(({ action, label }) => (
              <div className="binding-row" key={action}>
                <span>{label}</span>
                <button
                  type="button"
                  className={`binding-button${listening === action ? ' binding-button--listening' : ''}`}
                  onClick={() => setListening((current) => current === action ? null : action)}
                  aria-label={`${label}: ${formatBinding(settings.keyBindings[action])}. Click to change.`}
                >
                  {listening === action ? 'PRESS A KEY…' : formatBinding(settings.keyBindings[action])}
                </button>
              </div>
            ))}
          </div>
          <p className="binding-help" aria-live="polite">
            {listening ? 'Listening now. Backspace restores this control to its default.' : 'If a key is already in use, the two controls swap places.'}
          </p>
        </div>

        <div className="settings-section">
          <div className="settings-section__heading"><div><h3>Game feel</h3><p>These preferences are saved on this device.</p></div></div>
          <div className="toggle-list">
            <ToggleRow checked={settings.showGhost} onChange={controls.setShowGhost} label="Ghost piece" description="Show where the current piece will land." />
            <ToggleRow checked={settings.motionEffects} onChange={controls.setMotionEffects} label="Motion effects" description="Keep background movement and feedback animations." />
            <ToggleRow checked={settings.touchHaptics} onChange={controls.setTouchHaptics} label="Touch vibration" description="Use subtle vibration on supported mobile devices." />
          </div>
        </div>
        <button type="button" className="primary-button settings-done" onClick={onClose}>Done</button>
      </section>
    </div>
  );
}
